import { create } from 'zustand';
import { apiFetchAuthed } from '../apiHelpers';
import { enumerateDays, shiftDay } from '../utils/date-utils';
import { todayYmd } from '../Schedule/day-utils';
import {
  ScheduleRangeResponse,
  CrewScheduleRangeResponse,
  ScheduleEvent,
  ScheduleStaff,
  CrewAssignment,
  CrewShift,
  OOOEntry,
  UnavailabilityBlock,
  PendingChange,
  PendingBlockChange,
  ShowCounts,
} from '../Schedule/types';

/** Normalize date strings to YYYY-MM-DD (strip any time component) */
const normDate = (d: string) => (d ? d.slice(0, 10) : d);

const normalizeOOO = (entries: OOOEntry[]): OOOEntry[] =>
  entries.map(o => ({
    ...o,
    start_date: normDate(o.start_date),
    end_date: normDate(o.end_date),
  }));

const normalizeEvents = (events: ScheduleEvent[]): ScheduleEvent[] =>
  events.map(e => ({
    ...e,
    event_date: normDate(e.event_date),
    end_date: e.end_date ? normDate(e.end_date) : null,
  }));

const normalizeShifts = (shifts: CrewShift[]): CrewShift[] =>
  (shifts || []).map(s => ({
    ...s,
    start_date: normDate(s.start_date),
    end_date: normDate(s.end_date),
  }));

const normalizeBlocks = (blocks: UnavailabilityBlock[]): UnavailabilityBlock[] =>
  (blocks || []).map(b => ({
    ...b,
    start_date: normDate(b.start_date),
    end_date: normDate(b.end_date),
  }));

/**
 * Transient state for the in-progress drag-extend of a shift's left or right edge.
 * Lives in the store (rather than component state) so any cell can ask "am I
 * inside the current drag preview?" and render its ghost bar accordingly.
 *
 * On pointerup, the diff between original and preview is converted into a batch
 * of dated `add`/`remove` pending changes — no new action type needed.
 */
export interface DragState {
  userId: string;
  /**
   * For shift drags this is the event being extended. For block drags
   * (`kind === 'block'`) block bars belong to the user not an event, so this
   * is `null`. Consumers MUST guard on `kind` before reading.
   */
  eventId: string | null;
  /** Edge being dragged. The opposite edge anchors the preview. */
  edge: 'left' | 'right';
  /** Anchor date — the edge that ISN'T moving. */
  anchorDate: string;
  /** Original shift extent (used to compute the diff on commit). */
  originalStart: string;
  originalEnd: string;
  /** Current pointer-mapped target date for the moving edge. */
  previewDate: string;
  /** True if previewDate would violate the event range or cross another event. */
  invalid: boolean;
  /**
   * Drag target type. Defaults to 'shift' (existing behavior); 'block' means
   * the commit writes to pendingBlockChanges and routes to /unavailability.
   */
  kind?: 'shift' | 'block';
}

interface ScheduleState {
  // Current view — a continuous window, NOT a month (ff-feedback item 9).
  //
  // `days` is the single source of truth for which columns the grid has. It used
  // to be (year, month, daysInMonth) with every consumer rebuilding date strings
  // as `${year}-${month}-${day}`, which is the only place a month boundary was
  // ever load-bearing: a day-of-month integer is meaningless without one, while a
  // date string is globally unique. Derive from `days`; never reconstruct.
  rangeStart: string;
  rangeEnd: string;
  days: string[];

  // Data from API
  events: ScheduleEvent[];
  staff: ScheduleStaff[];
  assignments: CrewAssignment[];
  shifts: CrewShift[];
  ooo: OOOEntry[];
  unavailableBlocks: UnavailabilityBlock[];
  showCounts: ShowCounts;

  // Dirty state from drag-and-drop
  pendingChanges: PendingChange[];
  /** Parallel queue for block create/delete pending changes. Save flow flushes
   *  these via POST/DELETE /unavailability BEFORE /reassign, so that any
   *  same-batch attempt to also shift the user on the blocked day correctly
   *  trips the backend hard-block check. */
  pendingBlockChanges: PendingBlockChange[];

  // Transient drag-extend state (null when no drag in progress)
  drag: DragState | null;

  // UI state
  loading: boolean;
  saving: boolean;
  /** Hard failure (fetch/network/auth) — rendered as an alarm-red banner. */
  error: string | null;
  /**
   * Partial-save outcome ("Saved 4 of 5 — 1 still pending …") — rendered in a
   * non-alarming amber banner. Distinct from `error`: a partial save is mostly
   * success, and painting it red makes the user think everything failed.
   */
  saveSummary: string | null;

  // Actions
  fetchRange: (
    from: string,
    to: string,
    onUnauthorized?: () => void,
    opts?: { quiet?: boolean; preservePending?: boolean }
  ) => Promise<void>;
  /**
   * The crew-facing read (items 7+14). Hits /schedule/crew-range, whose payload
   * is redacted server-side, and maps it into the SAME state the producer grid
   * renders from — so ScheduleGrid stays one component with two consumers.
   *
   * Time off and scheduler blocks arrive already merged into one neutral
   * `unavailable` array. They land in `unavailableBlocks` with empty notes,
   * which is what makes the existing block bar render as a plain "unavailable"
   * stripe with no tooltip text. `ooo` stays empty on purpose: OOO bars carry a
   * reason in the producer view, and there is no crew variant of that.
   */
  fetchCrewRange: (from: string, to: string, onUnauthorized?: () => void) => Promise<void>;
  addPendingChange: (change: PendingChange) => void;
  removePendingChange: (id: string) => void;
  clearPendingChanges: () => void;
  saveChanges: (onUnauthorized?: () => void) => Promise<void>;
  addOOO: (entry: Omit<OOOEntry, 'id'>, onUnauthorized?: () => void) => Promise<void>;
  updateOOO: (id: string, updates: Partial<OOOEntry>, onUnauthorized?: () => void) => Promise<void>;
  deleteOOO: (id: string, onUnauthorized?: () => void) => Promise<void>;
  // Unavailability blocks (scheduler-authored)
  addPendingBlockChange: (change: PendingBlockChange) => void;
  removePendingBlockChange: (id: string) => void;
  updatePendingBlockNotes: (id: string, notes: string) => void;
  // Drag-extend actions
  startDrag: (drag: DragState) => void;
  updateDrag: (previewDate: string, invalid: boolean) => void;
  commitDrag: () => void;
  cancelDrag: () => void;
}

/**
 * The default window, mirroring the tour timeline's 90-day scroll because
 * "format it the same way as the tour timeline" IS the request (item 9).
 *
 * One deliberate divergence: the tour timeline starts at today, since a truck's
 * past bookings don't matter. A staff schedule's recent past does — the Mo/90d
 * column exists precisely to show recent load — so this reaches back two weeks.
 * Total span stays well under the endpoint's 400-day cap.
 */
export const WINDOW_BACK_DAYS = 14;
export const WINDOW_FORWARD_DAYS = 120;

export const defaultWindow = (anchor: string = todayYmd()) => ({
  start: shiftDay(anchor, -WINDOW_BACK_DAYS),
  end: shiftDay(anchor, WINDOW_FORWARD_DAYS),
});

/** One person-swap, reassembled from its tagged remove+add pair. */
export interface CollectedMove {
  event_id: string;
  from_user_id: string;
  to_user_id: string;
  start_date: string;
  end_date: string;
  event_name?: string;
  to_user_name?: string;
  /** Every pending item this move consumed, so a failure re-pends them together. */
  itemIds: string[];
}

/**
 * Reassemble tagged remove+add pairs into atomic moves.
 *
 * A group qualifies only if it is a COMPLETE, unambiguous swap: one source
 * person, one target person, one event, the same day set on both halves, and
 * those days contiguous. Anything else — a half undone, a hand-edited queue,
 * two drops sharing a tag — is returned in `leftovers` and takes the ordinary
 * remove/add phases. That fallback is the pre-existing behavior, so a group
 * this refuses is never worse off than before moves existed, and this function
 * never has to guess.
 */
export function collectMoves(changes: PendingChange[]): {
  moves: CollectedMove[];
  leftovers: PendingChange[];
} {
  const groups = new Map<string, PendingChange[]>();
  const leftovers: PendingChange[] = [];
  for (const c of changes) {
    if (!c.moveId) { leftovers.push(c); continue; }
    if (!groups.has(c.moveId)) groups.set(c.moveId, []);
    groups.get(c.moveId)!.push(c);
  }

  const moves: CollectedMove[] = [];
  for (const items of groups.values()) {
    const removes = items.filter((c) => c.action === 'remove');
    const adds = items.filter((c) => c.action === 'add');
    const fromIds = new Set(removes.map((c) => c.user_id));
    const toIds = new Set(adds.map((c) => c.user_id));
    const eventIds = new Set(items.map((c) => c.event_id));

    const removeDays = removes.map((c) => c.date).filter(Boolean).sort() as string[];
    const addDays = adds.map((c) => c.date).filter(Boolean).sort() as string[];

    const wellFormed =
      removes.length > 0 &&
      adds.length > 0 &&
      fromIds.size === 1 &&
      toIds.size === 1 &&
      eventIds.size === 1 &&
      [...fromIds][0] !== [...toIds][0] &&
      removeDays.length === removes.length &&
      addDays.length === adds.length &&
      removeDays.join() === addDays.join() &&
      // Contiguous: N days spanning exactly N-1 steps end to end.
      enumerateDays(removeDays[0], removeDays[removeDays.length - 1]).join() === removeDays.join();

    if (!wellFormed) { leftovers.push(...items); continue; }

    moves.push({
      event_id: [...eventIds][0],
      from_user_id: [...fromIds][0],
      to_user_id: [...toIds][0],
      start_date: removeDays[0],
      end_date: removeDays[removeDays.length - 1],
      event_name: items[0].event_name,
      to_user_name: adds[0].user_name,
      itemIds: items.map((c) => c.id),
    });
  }

  return { moves, leftovers };
}

const INITIAL = defaultWindow();

const useScheduleStore = create<ScheduleState>((set, get) => ({
  rangeStart: INITIAL.start,
  rangeEnd: INITIAL.end,
  days: enumerateDays(INITIAL.start, INITIAL.end),
  events: [],
  staff: [],
  assignments: [],
  shifts: [],
  ooo: [],
  unavailableBlocks: [],
  showCounts: { monthly: {}, trailing: {} },
  pendingChanges: [],
  pendingBlockChanges: [],
  drag: null,
  loading: false,
  saving: false,
  error: null,
  saveSummary: null,

  fetchRange: async (from, to, onUnauthorized, opts = {}) => {
    // `quiet`: don't toggle the page-level `loading` flag. StaffSchedulePage
    // unmounts the whole grid while loading, which destroys the horizontal
    // scroll position — a post-save refetch must NOT do that or the view jumps
    // back to the start of the window. That mattered when a month was the whole
    // view; with a 134-day window it matters more, because there is far more
    // scroll position to lose. `preservePending`: leave the pending queues
    // untouched so saveChanges can re-seat only the un-committed changes itself
    // instead of having them wiped out from under a partial failure.
    const { quiet = false, preservePending = false } = opts;
    // Non-quiet fetches are user-initiated navigation/refresh — clear any stale
    // partial-save banner. The quiet (post-save) path leaves saveSummary alone;
    // saveChanges sets it after the refetch returns.
    if (!quiet) set({ loading: true, error: null, saveSummary: null });
    try {
      const data: ScheduleRangeResponse = await apiFetchAuthed(
        `/schedule/range?from=${from}&to=${to}`,
        { onUnauthorized }
      );
      // Trust the window WE asked for, not the echo. They are the same today, but
      // deriving columns from the response would make a slow request that resolves
      // after a newer one repaint the grid to a stale window.
      set({
        rangeStart: from,
        rangeEnd: to,
        days: enumerateDays(from, to),
        events: normalizeEvents(data.events),
        staff: data.staff,
        assignments: data.assignments,
        shifts: normalizeShifts(data.shifts),
        ooo: normalizeOOO(data.ooo),
        unavailableBlocks: normalizeBlocks(data.blocks || []),
        showCounts: data.showCounts,
        ...(quiet ? {} : { loading: false }),
        ...(preservePending ? {} : { pendingChanges: [], pendingBlockChanges: [] }),
      });
    } catch (err: any) {
      set(quiet ? { error: err.message } : { loading: false, error: err.message });
    }
  },

  fetchCrewRange: async (from, to, onUnauthorized) => {
    set({ loading: true, error: null, saveSummary: null });
    try {
      const data: CrewScheduleRangeResponse = await apiFetchAuthed(
        `/schedule/crew-range?from=${from}&to=${to}`,
        { onUnauthorized }
      );
      set({
        rangeStart: from,
        rangeEnd: to,
        days: enumerateDays(from, to),
        events: normalizeEvents(data.events),
        staff: data.staff,
        assignments: data.assignments,
        shifts: normalizeShifts(data.shifts),
        // No OOO bars in the crew view — an OOO bar means "time off, here's why".
        ooo: [],
        unavailableBlocks: (data.unavailable || []).map((u, i) => ({
          // Synthetic id: nothing in this view can delete a block, and the real
          // ids are not sent. Index-based is fine because it is never a key for
          // anything that outlives this payload.
          id: `crew-unavail-${i}`,
          user_id: u.user_id,
          start_date: normDate(u.start_date),
          end_date: normDate(u.end_date),
          notes: '',
        })),
        // The crew payload carries no showCounts. Zeroing it rather than leaving
        // stale producer numbers behind, since one store serves both pages.
        showCounts: { monthly: {}, trailing: {} },
        loading: false,
        pendingChanges: [],
        pendingBlockChanges: [],
      });
    } catch (err: any) {
      set({ loading: false, error: err.message });
    }
  },

  addPendingChange: (change) => {
    set((state) => {
      // Match key: dated changes are matched per-day; dateless changes match
      // any change for the same (event, user) pair. Mixing the two for the
      // same (event, user) — e.g. one path emits dated and another emits
      // dateless — is currently an invariant: the scheduler popover always
      // emits dated, the event-form crew editor always emits dateless. Warn
      // in dev if anything ever breaks that assumption.
      if (process.env.NODE_ENV !== 'production') {
        const mixed = state.pendingChanges.find(
          (c) =>
            c.event_id === change.event_id &&
            c.user_id === change.user_id &&
            (c.date === undefined) !== (change.date === undefined)
        );
        if (mixed) {
          // eslint-disable-next-line no-console
          console.warn(
            '[useScheduleStore] Mixing dated and dateless pending changes for the same (event, user). ' +
              'These will NOT cancel each other and both will be sent to the backend on save.',
            { existing: mixed, incoming: change }
          );
        }
      }

      const sameSlot = (c: PendingChange) =>
        c.event_id === change.event_id &&
        c.user_id === change.user_id &&
        c.date === change.date;

      // Opposite action on the same slot cancels both (undoes the pending click)
      const opposing = state.pendingChanges.find(
        (c) => sameSlot(c) && c.action !== change.action
      );
      if (opposing) {
        return {
          pendingChanges: state.pendingChanges.filter((c) => c.id !== opposing.id),
        };
      }

      // Same action on the same slot — already queued, skip
      const duplicate = state.pendingChanges.find(
        (c) => sameSlot(c) && c.action === change.action
      );
      if (duplicate) return state;

      return { pendingChanges: [...state.pendingChanges, change] };
    });
  },

  removePendingChange: (id) => {
    set((state) => ({
      pendingChanges: state.pendingChanges.filter((c) => c.id !== id),
    }));
  },

  startDrag: (drag) => set({ drag }),

  updateDrag: (previewDate, invalid) =>
    set((state) =>
      state.drag ? { drag: { ...state.drag, previewDate, invalid } } : {}
    ),

  cancelDrag: () => set({ drag: null }),

  commitDrag: () => {
    const state = get();
    const d = state.drag;
    if (!d || d.invalid) {
      set({ drag: null });
      return;
    }

    // Compute new shift/block extent based on which edge moved.
    let newStart: string;
    let newEnd: string;
    if (d.edge === 'right') {
      newStart = d.anchorDate;
      newEnd = d.previewDate;
    } else {
      newStart = d.previewDate;
      newEnd = d.anchorDate;
    }
    if (newEnd < newStart) {
      // Pointer dragged past the anchor — clamp to single-day at the anchor.
      newStart = newEnd = d.anchorDate;
    }

    const oldDays = new Set(enumerateDays(d.originalStart, d.originalEnd));
    const newDays = new Set(enumerateDays(newStart, newEnd));
    const toAdd = [...newDays].filter((day) => !oldDays.has(day));
    const toRemove = [...oldDays].filter((day) => !newDays.has(day));
    const stamp = Date.now();

    if (d.kind === 'block') {
      // Block drags only support EXTENDING / SHRINKING a pending-add block
      // (i.e. before save). The per-day adds/removes get pushed through
      // addPendingBlockChange so opposite-action pairs cancel cleanly — a
      // shrink drag deletes the prior pending-add for those days, leaving
      // the right pending state. Once a block is saved, its bar is rendered
      // without drag handles (see ScheduleCell).
      set({ drag: null });
      const adds: PendingBlockChange[] = toAdd.map((day, i) => ({
        id: `block-drag-add-${stamp}-${i}`,
        action: 'add' as const,
        user_id: d.userId,
        date: day,
      }));
      const removes: PendingBlockChange[] = toRemove.map((day, i) => ({
        id: `block-drag-rm-${stamp}-${i}`,
        action: 'remove' as const,
        user_id: d.userId,
        date: day,
      }));
      const add = get().addPendingBlockChange;
      [...adds, ...removes].forEach((c) => add(c));
      return;
    }

    // Default: shift drag. Add the new days, remove the old ones — backend
    // batchReassign handles merge-on-adjacent and split-on-remove.
    const newChanges: PendingChange[] = [
      ...toAdd.map((day, i) => ({
        id: `drag-add-${stamp}-${i}`,
        action: 'add' as const,
        event_id: d.eventId,
        user_id: d.userId,
        date: day,
      })),
      ...toRemove.map((day, i) => ({
        id: `drag-rm-${stamp}-${i}`,
        action: 'remove' as const,
        event_id: d.eventId,
        user_id: d.userId,
        date: day,
      })),
    ];

    set((s) => ({
      drag: null,
      pendingChanges: [...s.pendingChanges, ...newChanges],
    }));
  },

  clearPendingChanges: () => {
    set({ pendingChanges: [], pendingBlockChanges: [] });
  },

  addPendingBlockChange: (change) => {
    set((state) => {
      // Saved-block removes are keyed by blockId — never by (user, date) —
      // so they don't accidentally cancel a pending-add that happens to share
      // the block's blockStart day. Adds (no blockId) and drag-shrink removes
      // (no blockId) still use the (user, date, action) matching that lets
      // opposing actions cancel each other inside one editing session.
      if (change.action === 'remove' && change.blockId) {
        const dupe = state.pendingBlockChanges.find(
          (c) => c.action === 'remove' && c.blockId === change.blockId
        );
        if (dupe) return state;
        return { pendingBlockChanges: [...state.pendingBlockChanges, change] };
      }
      const sameSlot = (c: PendingBlockChange) =>
        c.user_id === change.user_id && c.date === change.date && !c.blockId;
      const opposing = state.pendingBlockChanges.find(
        (c) => sameSlot(c) && c.action !== change.action
      );
      if (opposing) {
        return {
          pendingBlockChanges: state.pendingBlockChanges.filter((c) => c.id !== opposing.id),
        };
      }
      const dupe = state.pendingBlockChanges.find(
        (c) => sameSlot(c) && c.action === change.action
      );
      if (dupe) return state;
      return { pendingBlockChanges: [...state.pendingBlockChanges, change] };
    });
  },

  removePendingBlockChange: (id) => {
    set((state) => ({
      pendingBlockChanges: state.pendingBlockChanges.filter((c) => c.id !== id),
    }));
  },

  updatePendingBlockNotes: (id, notes) => {
    set((state) => ({
      pendingBlockChanges: state.pendingBlockChanges.map((c) =>
        c.id === id ? { ...c, notes } : c
      ),
    }));
  },

  saveChanges: async (onUnauthorized) => {
    const { pendingChanges, pendingBlockChanges, rangeStart, rangeEnd } = get();
    if (!pendingChanges.length && !pendingBlockChanges.length) return;

    set({ saving: true, error: null, saveSummary: null });

    // Resilient save: a failure in ANY single operation must never silently
    // discard the others. We track which source items did NOT commit (by id)
    // so they stay pending and the user can fix-and-retry instead of losing
    // work. `failures` collects human-readable reasons for the save-bar summary.
    const failedShiftIds = new Set<string>();
    const failedBlockIds = new Set<string>();
    const failures: string[] = [];

    const firstName = (n?: string) => (n ? n.split(' ')[0] : 'Crew');
    const describeShift = (c: PendingChange, reason: string) =>
      `${firstName(c.user_name)} ${c.action === 'add' ? '→' : '⁄'} ${c.event_name || 'event'}` +
      `${c.date ? ` (${c.date})` : ''}: ${reason}`;
    const dayRange = (start: string, end: string) => (end !== start ? `${start}–${end}` : start);

    // One /reassign call for a subset of shift changes. A whole-call failure
    // (network/5xx) keeps every change in the subset pending; per-change
    // failures reported back in results.errors[] keep only those pending —
    // the rest are considered committed and cleared. (The backend isolates
    // each change in its own savepoint, so a single bad change can't poison
    // its neighbors; we just have to actually READ the errors it reports.)
    const runReassign = async (subset: PendingChange[]) => {
      if (!subset.length) return;
      let results: { errors?: Array<{ change?: any; error: string }> } | null = null;
      try {
        results = await apiFetchAuthed('/schedule/reassign', {
          method: 'POST',
          body: {
            changes: subset.map((c) => ({
              action: c.action,
              event_id: c.event_id,
              user_id: c.user_id,
              role: c.role || '',
              ...(c.date ? { date: c.date } : {}),
            })),
          },
          onUnauthorized,
        });
      } catch (err: any) {
        subset.forEach((c) => failedShiftIds.add(c.id));
        failures.push(err.message || 'Shift changes could not be saved');
        return;
      }
      for (const e of results?.errors || []) {
        const ch = e.change || {};
        const match = subset.find(
          (c) =>
            !failedShiftIds.has(c.id) &&
            c.action === ch.action &&
            c.event_id === ch.event_id &&
            c.user_id === ch.user_id &&
            (c.date || undefined) === (ch.date || undefined)
        );
        if (match) {
          failedShiftIds.add(match.id);
          failures.push(describeShift(match, e.error));
        }
      }
    };

    // Global write order: shift REMOVES → unavailability blocks → person MOVES
    // → shift ADDS.
    //  - Removes first vacates the day, so creating a block on a just-freed day
    //    no longer trips the backend's SHIFT_CONFLICT check (this is the
    //    "remove someone AND mark them unavailable" bug).
    //  - Blocks before adds preserves the intended "a block beats an attempt to
    //    also assign that person on the blocked day" rule — the adds-phase
    //    reassign re-reads blocks and hard-rejects such adds.
    //  - MOVES SIT WITH THE ADDS, NOT BEFORE THE REMOVES. A move carries an add
    //    half, so it obeys the same two rules: it must see a remove that frees
    //    its target (or it 409s citing a conflict the producer already resolved
    //    on screen), and it must lose to a block created in the same save (or
    //    an assignment beats an unavailability, which inverts the rule above).
    //
    //    KNOWN RESIDUAL, and it is a genuine trade-off rather than an oversight.
    //    A move is atomic, so its remove half cannot also run in phase 1. Marking
    //    the SOURCE person unavailable on the very days a swap takes off them, in
    //    ONE save, fails the block with SHIFT_CONFLICT: the block is created
    //    before the move frees them. The block stays pending and saves on a
    //    second click. That direction was chosen because the alternative lets an
    //    assignment beat a block, and a block losing is a scheduling-safety
    //    failure while a retry is an inconvenience.
    //
    // Trade-off (intentional): this is now THREE server round-trips instead of
    // one atomic reassign. Removes can commit while adds later fail (the failed
    // adds stay pending and are retryable, so no data is lost — but the server
    // can briefly hold a vacated-but-not-yet-reassigned state). The GCal sync in
    // the router also fires once per /reassign call, so an event touched by both
    // a remove and an add is synced twice (fire-and-forget + idempotent, so
    // harmless). Do NOT collapse this back into a single call without restoring
    // the SHIFT_CONFLICT-safe ordering some other way.
    // Pure partition, done up front so the phases below agree on what counts
    // as a loose change. The network calls for `moves` happen in phase 2b.
    const { moves, leftovers } = collectMoves(pendingChanges);
    const removes = leftovers.filter((c) => c.action === 'remove');
    const adds = leftovers.filter((c) => c.action === 'add');

    // ── Phase 1: shift removes ──
    await runReassign(removes);

    // ── Phase 2: unavailability block creates / deletes ──
    if (pendingBlockChanges.length) {
      // Adds: group by user, collapse each user's days into CONTIGUOUS RUNS,
      // one POST per run. First non-empty note per run wins (multi-note merge
      // is a v1.1 item — see docs/features/crew-unavailability-blocks.md). We
      // now also carry each day's source item id so a failed run's days stay
      // pending instead of vanishing.
      const addsByUser = new Map<string, Map<string, { notes: string; id: string }>>();
      const removeItems: PendingBlockChange[] = [];
      for (const pc of pendingBlockChanges) {
        if (pc.action === 'add') {
          if (!addsByUser.has(pc.user_id)) addsByUser.set(pc.user_id, new Map());
          addsByUser.get(pc.user_id)!.set(pc.date, { notes: pc.notes || '', id: pc.id });
        } else {
          removeItems.push(pc);
        }
      }

      for (const [user_id, dateInfo] of addsByUser.entries()) {
        const sortedDates = [...dateInfo.keys()].sort();
        let runStart: string | null = null;
        let runEnd: string | null = null;
        let runNotes = '';
        let runIds: string[] = [];
        const flushRun = async () => {
          if (!runStart || !runEnd) return;
          const start = runStart;
          const end = runEnd;
          const notes = runNotes;
          const ids = runIds;
          runStart = null;
          runEnd = null;
          runNotes = '';
          runIds = [];
          try {
            await apiFetchAuthed('/schedule/unavailability', {
              method: 'POST',
              body: { user_id, start_date: start, end_date: end, notes },
              onUnauthorized,
            });
          } catch (err: any) {
            ids.forEach((id) => failedBlockIds.add(id));
            failures.push(`Mark unavailable ${dayRange(start, end)}: ${err.message || 'could not save'}`);
          }
        };
        for (const date of sortedDates) {
          const info = dateInfo.get(date)!;
          if (!runStart) {
            runStart = date;
            runEnd = date;
            runNotes = info.notes;
            runIds = [info.id];
          } else if (shiftDay(runEnd!, 1) === date) {
            runEnd = date;
            runIds.push(info.id);
            if (info.notes && !runNotes) runNotes = info.notes;
          } else {
            await flushRun();
            runStart = date;
            runEnd = date;
            runNotes = info.notes;
            runIds = [info.id];
          }
        }
        await flushRun();
      }

      // Removes: walk each by block id (FE pre-resolves blockId at remove time).
      for (const pc of removeItems) {
        if (!pc.blockId) continue;
        try {
          await apiFetchAuthed(`/schedule/unavailability/${pc.blockId}`, {
            method: 'DELETE',
            onUnauthorized,
          });
        } catch (err: any) {
          failedBlockIds.add(pc.id);
          failures.push(`Remove unavailability: ${err.message || 'could not save'}`);
        }
      }
    }

    // ── Phase 2b: person swaps, each as ONE atomic /schedule/move ──
    // Runs AFTER removes and blocks, and before the loose adds — see the write
    // order at the top of this function for why that position is load-bearing.
    //
    // A swap arrives as a tagged remove+add pair per day so the grid preview
    // and undo stay unchanged, but it must not be SENT that way: /reassign
    // savepoints each change, so a refused add leaves the source person
    // already removed and an "unassigned" email already sent. Only complete,
    // contiguous groups qualify; anything else falls through to the ordinary
    // phases, which is the pre-existing behavior.
    for (const mv of moves) {
      try {
        await apiFetchAuthed('/schedule/move', {
          method: 'POST',
          body: {
            event_id: mv.event_id,
            from_user_id: mv.from_user_id,
            to_user_id: mv.to_user_id,
            start_date: mv.start_date,
            end_date: mv.end_date,
          },
          onUnauthorized,
        });
      } catch (err: any) {
        // The move is all-or-nothing server-side, so every item in the group
        // stays pending together and the producer can retry or undo it whole.
        mv.itemIds.forEach((id) => failedShiftIds.add(id));
        failures.push(
          `${firstName(mv.to_user_name)} ⁄ ${mv.event_name || 'event'} ` +
          `(${dayRange(mv.start_date, mv.end_date)}): ${err.message || 'move could not be saved'}`
        );
      }
    }

    // ── Phase 3: shift adds ──
    await runReassign(adds);

    // Reconcile. Pull authoritative data WITHOUT tearing down the grid (quiet:
    // no loading flip → scroll/anchor preserved) and WITHOUT wiping the queues
    // (preservePending), then drop only the changes that committed.
    try {
      await get().fetchRange(rangeStart, rangeEnd, onUnauthorized, { quiet: true, preservePending: true });
    } catch {
      // Quiet refetch failed (network/auth). Server writes already landed; the
      // view is just stale until the next fetch. Don't lose the pending reseat.
    }

    const total = pendingChanges.length + pendingBlockChanges.length;
    const failedCount = failedShiftIds.size + failedBlockIds.size;
    let summary: string | null = null;
    if (failedCount) {
      const shown = failures.slice(0, 3).join('  •  ');
      const more = failures.length > 3 ? `  (+${failures.length - 3} more)` : '';
      summary =
        `Saved ${total - failedCount} of ${total} change${total !== 1 ? 's' : ''}. ` +
        `${failedCount} still pending — ${shown}${more}`;
    }

    // Functional update: keep (a) anything queued AFTER this save started — the
    // grid stays mounted during save now, so the user can drag in new changes
    // mid-flight and we must not clobber them — and (b) submitted items that
    // failed. Drop only submitted items that committed. Diffing by id against
    // the pre-save snapshot is what makes the concurrent-edit case safe.
    const submittedShiftIds = new Set(pendingChanges.map((c) => c.id));
    const submittedBlockIds = new Set(pendingBlockChanges.map((c) => c.id));
    set((s) => ({
      saving: false,
      pendingChanges: s.pendingChanges.filter(
        (c) => !submittedShiftIds.has(c.id) || failedShiftIds.has(c.id)
      ),
      pendingBlockChanges: s.pendingBlockChanges.filter(
        (c) => !submittedBlockIds.has(c.id) || failedBlockIds.has(c.id)
      ),
      saveSummary: summary,
    }));
  },

  addOOO: async (entry, onUnauthorized) => {
    try {
      await apiFetchAuthed('/schedule/ooo', {
        method: 'POST',
        body: entry,
        onUnauthorized,
      });
      const { rangeStart, rangeEnd } = get();
      // quiet + preservePending, exactly as saveChanges does. Without quiet, the
      // loading flag unmounts the grid (StaffSchedulePage renders it on !loading),
      // which resets ScheduleGrid's hasAutoScrolled ref on remount and snaps the
      // view back to today — ~100 columns of lost position now that scrolling is
      // the ONLY way to move through time. Without preservePending, editing an
      // unrelated OOO silently discarded every queued shift drag.
      await get().fetchRange(rangeStart, rangeEnd, onUnauthorized, { quiet: true, preservePending: true });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  updateOOO: async (id, updates, onUnauthorized) => {
    try {
      await apiFetchAuthed(`/schedule/ooo/${id}`, {
        method: 'PATCH',
        body: updates,
        onUnauthorized,
      });
      const { rangeStart, rangeEnd } = get();
      // quiet + preservePending, exactly as saveChanges does. Without quiet, the
      // loading flag unmounts the grid (StaffSchedulePage renders it on !loading),
      // which resets ScheduleGrid's hasAutoScrolled ref on remount and snaps the
      // view back to today — ~100 columns of lost position now that scrolling is
      // the ONLY way to move through time. Without preservePending, editing an
      // unrelated OOO silently discarded every queued shift drag.
      await get().fetchRange(rangeStart, rangeEnd, onUnauthorized, { quiet: true, preservePending: true });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  deleteOOO: async (id, onUnauthorized) => {
    try {
      await apiFetchAuthed(`/schedule/ooo/${id}`, {
        method: 'DELETE',
        onUnauthorized,
      });
      const { rangeStart, rangeEnd } = get();
      // quiet + preservePending, exactly as saveChanges does. Without quiet, the
      // loading flag unmounts the grid (StaffSchedulePage renders it on !loading),
      // which resets ScheduleGrid's hasAutoScrolled ref on remount and snaps the
      // view back to today — ~100 columns of lost position now that scrolling is
      // the ONLY way to move through time. Without preservePending, editing an
      // unrelated OOO silently discarded every queued shift drag.
      await get().fetchRange(rangeStart, rangeEnd, onUnauthorized, { quiet: true, preservePending: true });
    } catch (err: any) {
      set({ error: err.message });
    }
  },
}));

/**
 * Compute effective assignments by merging DB assignments with pending changes.
 * Event-grain — used by the popover's "currently assigned to" list, which only
 * needs to know identity, not per-day coverage. A dateless pending change adds
 * or removes the whole assignment; a dated pending change adds a (provisional)
 * assignment but does NOT remove it on day-removals (since the assignment may
 * still cover other days).
 */
export function computeEffectiveAssignments(
  assignments: CrewAssignment[],
  pendingChanges: PendingChange[]
): CrewAssignment[] {
  let effective = [...assignments];

  for (const change of pendingChanges) {
    if (change.action === 'add') {
      const exists = effective.some(
        (a) => a.event_id === change.event_id && a.user_id === change.user_id
      );
      if (!exists) {
        effective.push({
          event_id: change.event_id,
          user_id: change.user_id,
          role: change.role || '',
          notes: '',
        });
      }
    } else if (change.action === 'remove' && !change.date) {
      effective = effective.filter(
        (a) => !(a.event_id === change.event_id && a.user_id === change.user_id)
      );
    }
  }

  return effective;
}

/**
 * Per-day cell bar descriptor — what a single cell should render for one
 * user on one date. `isPendingAdd` and `isPendingRemove` drive the dashed
 * border / strikethrough visuals in ScheduleCell.
 */
export interface DayBar {
  event_id: string;
  role: string;
  isPendingAdd: boolean;
  isPendingRemove: boolean;
  /** Start of the shift this bar belongs to (for drag-extend anchoring). */
  shiftStart: string;
  /** End of the shift this bar belongs to. */
  shiftEnd: string;
}

/**
 * Build the lookup `Map<user_id, Map<dateStr, DayBar[]>>` used by the grid.
 * Each entry is "this user has these event-bars to show on this date."
 *
 * Sources:
 *  - Real shifts (DB) → bar on every date the shift covers, role from
 *    matching event_crew assignment.
 *  - Pending shift-add (with date) → provisional bar, isPendingAdd=true.
 *  - Pending day-remove → original bar still shown but flagged isPendingRemove
 *    until save.
 *  - Pending dateless remove (event-form path) → removes all bars for that
 *    (event, user) pair — used when the legacy whole-event remove fires.
 */
export function computeEffectiveDayBars(
  shifts: CrewShift[],
  assignments: CrewAssignment[],
  pendingChanges: PendingChange[]
): Map<string, Map<string, DayBar[]>> {
  // user_id|event_id -> role (for shift bars, role lives on event_crew row)
  const roleByUserEvent = new Map<string, string>();
  for (const a of assignments) {
    roleByUserEvent.set(`${a.user_id}|${a.event_id}`, a.role || '');
  }

  // Pre-bucket pending changes by (user_id|event_id) for fast lookup
  type PCSet = { datedRemoves: Set<string>; datedAdds: Map<string, string>; datelessRemove: boolean };
  const pcByUserEvent = new Map<string, PCSet>();
  for (const pc of pendingChanges) {
    const key = `${pc.user_id}|${pc.event_id}`;
    if (!pcByUserEvent.has(key)) {
      pcByUserEvent.set(key, { datedRemoves: new Set(), datedAdds: new Map(), datelessRemove: false });
    }
    const set = pcByUserEvent.get(key)!;
    if (pc.action === 'add' && pc.date) {
      set.datedAdds.set(pc.date, pc.role || '');
      // The pending add implies a role for this user/event if no assignment exists yet
      if (!roleByUserEvent.has(key)) roleByUserEvent.set(key, pc.role || '');
    } else if (pc.action === 'remove' && pc.date) {
      set.datedRemoves.add(pc.date);
    } else if (pc.action === 'remove' && !pc.date) {
      set.datelessRemove = true;
    }
  }

  const result = new Map<string, Map<string, DayBar[]>>();
  const push = (userId: string, dateStr: string, bar: DayBar) => {
    let byDay = result.get(userId);
    if (!byDay) { byDay = new Map(); result.set(userId, byDay); }
    const arr = byDay.get(dateStr) || [];
    arr.push(bar);
    byDay.set(dateStr, arr);
  };

  // Expand each real shift across the days it covers
  for (const s of shifts) {
    const key = `${s.user_id}|${s.event_id}`;
    const pc = pcByUserEvent.get(key);
    if (pc?.datelessRemove) continue; // whole-event remove pending → skip all days

    const role = roleByUserEvent.get(key) || '';
    // Iterate days from start_date to end_date (inclusive, alphabetic = chronological)
    const start = s.start_date;
    const end = s.end_date;
    // Walk via Date math but stay UTC-clean
    const [sy, sm, sd] = start.split('-').map(Number);
    const dt = new Date(Date.UTC(sy, sm - 1, sd));
    while (true) {
      const ds = dt.toISOString().slice(0, 10);
      if (ds > end) break;
      const isPendingRemove = !!pc?.datedRemoves.has(ds);
      push(s.user_id, ds, {
        event_id: s.event_id,
        role,
        isPendingAdd: false,
        isPendingRemove,
        shiftStart: start,
        shiftEnd: end,
      });
      dt.setUTCDate(dt.getUTCDate() + 1);
    }
  }

  // Layer pending dated adds — bars on days not already covered by a real shift
  for (const [key, set] of pcByUserEvent.entries()) {
    if (set.datelessRemove) continue;
    const [userId, eventId] = key.split('|');
    for (const [date, role] of set.datedAdds.entries()) {
      const existing = result.get(userId)?.get(date) || [];
      if (existing.some(b => b.event_id === eventId)) continue;
      push(userId, date, {
        event_id: eventId,
        role: role || roleByUserEvent.get(key) || '',
        isPendingAdd: true,
        isPendingRemove: false,
        // Pending-add bars don't belong to a real shift yet — treat the
        // single day as the "shift extent" for drag-handle purposes.
        shiftStart: date,
        shiftEnd: date,
      });
    }
  }

  return result;
}

/**
 * Per-cell descriptor for a scheduler-authored unavailability block. Parallel
 * to DayBar but lives in its own map — block rendering is single-purpose
 * (no event, no role) and overloading DayBar with a kind discriminator would
 * leak nullable fields into every shift consumer.
 */
export interface BlockBar {
  /** Present for saved blocks; absent for pending-add bars. */
  id?: string;
  notes: string;
  isPendingAdd: boolean;
  isPendingRemove: boolean;
  /** Start of the contiguous block run this bar belongs to. */
  blockStart: string;
  /** End of the run (inclusive). */
  blockEnd: string;
}

/**
 * Build `Map<user_id, Map<dateStr, BlockBar>>` for grid rendering.
 *
 * Each cell hosts AT MOST one block bar (a user can't have two blocks
 * overlapping in time — enforced by cub_no_overlap in the DB). Pending-add
 * days collapse into a single bar covering each contiguous run, so drag
 * handles anchor to the run's outer edges.
 *
 * Pending-remove of a saved block by id (via blockId) skips the whole bar.
 * Per-day pending-remove on a pending-add range simply omits those days
 * (the cancel-opposing logic in addPendingBlockChange already strips the
 * matching add when a remove on the same (user, date) lands).
 */
export function computeEffectiveBlockBars(
  blocks: UnavailabilityBlock[],
  pendingChanges: PendingBlockChange[]
): Map<string, Map<string, BlockBar>> {
  type PCSet = {
    adds: Map<string, string>;
    removes: Set<string>;
    removedBlockIds: Set<string>;
  };
  const pendingByUser = new Map<string, PCSet>();
  for (const pc of pendingChanges) {
    if (!pendingByUser.has(pc.user_id)) {
      pendingByUser.set(pc.user_id, { adds: new Map(), removes: new Set(), removedBlockIds: new Set() });
    }
    const set = pendingByUser.get(pc.user_id)!;
    if (pc.action === 'add') {
      set.adds.set(pc.date, pc.notes || '');
    } else {
      set.removes.add(pc.date);
      if (pc.blockId) set.removedBlockIds.add(pc.blockId);
    }
  }

  const result = new Map<string, Map<string, BlockBar>>();
  const put = (userId: string, dateStr: string, bar: BlockBar) => {
    let byDay = result.get(userId);
    if (!byDay) {
      byDay = new Map();
      result.set(userId, byDay);
    }
    byDay.set(dateStr, bar);
  };

  // Layer saved blocks. A whole-block pending-remove paints line-through
  // across every day so the user can see the block is queued for deletion
  // before they hit Save (and Discard reverts cleanly).
  for (const b of blocks) {
    const pending = pendingByUser.get(b.user_id);
    const blockMarkedForDelete = !!pending?.removedBlockIds.has(b.id);
    const [sy, sm, sd] = b.start_date.split('-').map(Number);
    const dt = new Date(Date.UTC(sy, sm - 1, sd));
    while (true) {
      const ds = dt.toISOString().slice(0, 10);
      if (ds > b.end_date) break;
      const isPendingRemove = blockMarkedForDelete || pending?.removes.has(ds) || false;
      put(b.user_id, ds, {
        id: b.id,
        notes: b.notes,
        isPendingAdd: false,
        isPendingRemove,
        blockStart: b.start_date,
        blockEnd: b.end_date,
      });
      dt.setUTCDate(dt.getUTCDate() + 1);
    }
  }

  // Layer pending-add days as contiguous runs (with drag handles)
  for (const [userId, { adds }] of pendingByUser.entries()) {
    if (!adds.size) continue;
    const sortedDates = [...adds.keys()].sort();

    // Group contiguous days into runs
    const runs: Array<{ start: string; end: string; notes: string }> = [];
    let cur: { start: string; end: string; notes: string } | null = null;
    for (const date of sortedDates) {
      const note = adds.get(date) || '';
      if (!cur) {
        cur = { start: date, end: date, notes: note };
      } else if (shiftDay(cur.end, 1) === date) {
        cur.end = date;
        if (note && !cur.notes) cur.notes = note;
      } else {
        runs.push(cur);
        cur = { start: date, end: date, notes: note };
      }
    }
    if (cur) runs.push(cur);

    // Emit one BlockBar per day in each run
    for (const run of runs) {
      for (const ds of enumerateDays(run.start, run.end)) {
        const userMap = result.get(userId);
        // Don't overwrite a saved-block bar that already covers this day —
        // the GiST constraint will reject this on save, but visually we
        // prefer to show the saved block until the user resolves it.
        if (userMap?.has(ds)) continue;
        put(userId, ds, {
          notes: run.notes,
          isPendingAdd: true,
          isPendingRemove: false,
          blockStart: run.start,
          blockEnd: run.end,
        });
      }
    }
  }

  return result;
}

export default useScheduleStore;
